import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import CustomButton from '@/components/CustomButton'
import Ripple from '@/components/Ripple'
import { useCurrencyList } from '@/hooks'
import { toFixedNoRound, formatThousands } from '@/utils'
import UserPreferences, { type CurrencyType } from '@/utils/userPreferences'
import { Toast } from 'react-vant'
import CurrencySelectPopup from '@/components/CurrencySelectPopup'

export default React.memo(function Exchange() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { currencyList } = useCurrencyList()

  // 从币种（支付）- 使用缓存或默认值
  const [fromCurrency, setFromCurrency] = useState<CurrencyType>(() => 
    UserPreferences.getExchangeFromCurrency()
  )
  const [fromAmount, setFromAmount] = useState('')

  // 到币种（收到）- 使用缓存或默认值
  const [toCurrency, setToCurrency] = useState<CurrencyType>(() => 
    UserPreferences.getExchangeToCurrency()
  )
  const [toAmount, setToAmount] = useState('')

  // Popup 控制状态
  const [showCurrencyPopup, setShowCurrencyPopup] = useState(false)
  const [currencySelectType, setCurrencySelectType] = useState<'from' | 'to' | null>(null)

  // 获取当前币种信息
  const fromCurrencyInfo = useMemo(() => 
    currencyList.find(c => c.name === fromCurrency), 
    [currencyList, fromCurrency]
  )

  const toCurrencyInfo = useMemo(() => 
    currencyList.find(c => c.name === toCurrency), 
    [currencyList, toCurrency]
  )

  // 监听币种变化，保存到偏好设置
  useEffect(() => {
    UserPreferences.setExchangeFromCurrency(fromCurrency)
  }, [fromCurrency])

  useEffect(() => {
    UserPreferences.setExchangeToCurrency(toCurrency)
  }, [toCurrency])

  // 选择从币种
  const handleSelectFromCurrency = useCallback(() => {
    setCurrencySelectType('from')
    setShowCurrencyPopup(true)
  }, [])
  
  // 选择到币种
  const handleSelectToCurrency = useCallback(() => {
    setCurrencySelectType('to')
    setShowCurrencyPopup(true)
  }, [])

  // 处理币种选择
  const handleCurrencySelect = useCallback((currency: { symbol: string }) => {
    const symbol = currency.symbol as CurrencyType
    if (currencySelectType === 'from') {
      setFromCurrency(symbol)
      setFromAmount('')
    } else if (currencySelectType === 'to') {
      setToCurrency(symbol)
      setToAmount('')
    }
    setShowCurrencyPopup(false)
    setCurrencySelectType(null)
  }, [currencySelectType])

  // 设置最大金额
  const handleMaxFrom = useCallback(() => {
    if (fromCurrencyInfo) {
      setFromAmount(toFixedNoRound(fromCurrencyInfo.balance, 2))
      // 简单的1:1兑换率计算（实际应该从接口获取汇率）
      setToAmount(toFixedNoRound(fromCurrencyInfo.balance, 2))
    }
  }, [fromCurrencyInfo])

  // 从金额变化时自动计算到金额
  useEffect(() => {
    if (fromAmount && parseFloat(fromAmount) > 0) {
      // 简单的1:1兑换率（实际应该从接口获取汇率）
      setToAmount(fromAmount)
    } else {
      setToAmount('')
    }
  }, [fromAmount])

  // 检查表单是否有效（含可用余额校验）
  const isFormValid = useMemo(() => {
    const amount = parseFloat(fromAmount)
    const balance = Number(fromCurrencyInfo?.balance ?? 0)
    return (
      fromAmount &&
      amount > 0 &&
      amount <= balance &&
      toAmount &&
      parseFloat(toAmount) > 0 &&
      fromCurrency !== toCurrency
    )
  }, [fromAmount, toAmount, fromCurrency, toCurrency, fromCurrencyInfo?.balance])

  // 提交兑换
  const handleSubmit = useCallback(() => {
    if (!fromAmount || parseFloat(fromAmount) <= 0) {
      Toast.info(t('请输入兑换金额'))
      return
    }
    const balance = Number(fromCurrencyInfo?.balance ?? 0)
    if (parseFloat(fromAmount) > balance) {
      Toast.info(t('兑换金额不能大于可用余额'))
      return
    }
    if (fromCurrency === toCurrency) {
      Toast.info(t('不能兑换相同的币种'))
      return
    }
    
    // 跳转到确认页面
    navigate('/exchange/confirm', {
      state: {
        fromCurrency,
        fromAmount,
        toCurrency,
        toAmount,
        fromIcon: fromCurrencyInfo?.icon,
        toIcon: toCurrencyInfo?.icon
      }
    })
  }, [fromAmount, fromCurrency, toCurrency, toAmount, fromCurrencyInfo, toCurrencyInfo, navigate, t])

  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={<span className="text-3.75 font-medium text-black">{t('兑换')}</span>}
        rightChildren={
          <div className='flex items-center' onClick={() => navigate('/exchange/record')}>
            <div className='w-6 h-6'>
              <img src="/icon/record-icon.svg" />
            </div>
          </div>
        }
      />

      <div className="bg-white px-6 py-6">
        {/* 从（支付） */}
        <div className="mb-4">
          <div className="text-3.5 text-[#0F0F0F]/60 mb-3">{t('从')}</div>
          
          {/* 币种选择和金额 */}
          <div className="flex min-h-16.5 items-center justify-between bg-white rounded-2xl p-4 border-[0.5px] border-solid border-[#E9E9E9]">
            <div className="flex items-center flex-shrink-0" onClick={handleSelectFromCurrency}>
              <img src={fromCurrencyInfo?.icon} alt={fromCurrency} className="w-5 h-5 mr-3" />
              <div className="flex items-center">
                <span className="text-base font-bold text-black mr-2">{fromCurrency}</span>
                <img src="/icon/picker-down.svg" alt="" className="w-3" />
              </div>
            </div>
            
            <div className="flex items-center justify-end flex-1 min-w-0">
              <input
                type="number"
                value={fromAmount}
                onChange={(e) => setFromAmount(e.target.value)}
                placeholder="0.00"
                className="text-right text-5 font-semibold text-black placeholder-[#1A1A1A]/60 outline-none flex-1 min-w-0"
              />
              <Ripple className="ml-2 rounded-lg flex-shrink-0">
                <div
                  onClick={handleMaxFrom}
                  className="px-3 py-1 bg-white text-3 rounded-lg border-[0.5px] border-solid border-[#E9E9E9]"
                >
                  {t('最大')}
                </div>
              </Ripple>
            </div>
          </div>

          {/* 可用余额 */}
          <div className="text-3 text-[#979797] mt-2">
            {t('可用余额')}: <span className='text-black ml-2'>{formatThousands(fromCurrencyInfo?.balance || 0, 2, true)} {fromCurrency}</span>
          </div>
        </div>

        {/* 到（收到） */}
        <div className="mb-6">
          <div className="text-3.5 text-[#0F0F0F]/60 mb-3">{t('到')}</div>
           
          {/* 币种选择和金额 */}
          <div className="flex min-h-16.5 items-center justify-between bg-white border-[0.5px] border-solid border-[#E9E9E9] rounded-2xl p-4">
            <div className="flex items-center flex-shrink-0" onClick={handleSelectToCurrency}>
              <img src={toCurrencyInfo?.icon} alt={toCurrency} className="w-5 h-5 mr-3" />
              <div className="flex items-center">
                <span className="text-base font-bold text-black mr-2">{toCurrency}</span>
                <img src="/icon/picker-down.svg" alt="" className="w-3" />
              </div>
            </div>
            
            <div className="flex items-center justify-end flex-1 min-w-0">
              <input
                type="number"
                value={toAmount}
                readOnly
                placeholder="0.00"
                className="text-right text-5 font-semibold text-black placeholder-[#1A1A1A]/60 outline-none bg-transparent flex-1 min-w-0"
              />
            </div>
          </div>
        </div>

        {/* 提交按钮 */}
        <div className="mt-8">
          <CustomButton 
            onClick={handleSubmit}
            disabled={!isFormValid}
          >
            {t('提交')}
          </CustomButton>
        </div>
      </div>

      {/* 币种选择 Popup */}
      <CurrencySelectPopup
        visible={showCurrencyPopup}
        onClose={() => {
          setShowCurrencyPopup(false)
          setCurrencySelectType(null)
        }}
        onSelect={handleCurrencySelect}
        currencies={currencyList}
      />
    </PageLayout>
  )
})
